import { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Gauge as GaugeIcon,
  Info,
  Loader2,
  MapPin,
  PauseCircle,
  PlayCircle,
  Radio,
  WifiOff,
  Zap,
} from 'lucide-react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { getWebSocketClient } from '../../utils/websocket-client';
import {
  computeVfdValues,
  findByRole,
  getDriveState,
  hexRegister,
  VFD_STATUS_COLORS,
  type VfdComputedValue,
  type VfdDrive,
  type VfdDriveState,
  type VfdItem,
  vfdApi,
} from '../../utils/vfd';

interface VFDDrivesPanelProps {
  clientId: string;
  token: string;
  /** Pre-loaded drives (from the Live PLC Data wrapper) — avoids a second fetch. */
  initialDrives?: VfdDrive[];
}

type LiveSnapshot = {
  values: Record<string, number | null>;
  raws: Record<string, number>;
  errors: Record<string, string>;
  comm: string;
  timestamp: number;
};

type RuntimeStatus = { connected: boolean; comm?: string; error?: string | null; reconnecting?: boolean; at: number };

type TrendPoint = { t: number; frequency: number | null; current: number | null; power: number | null };

// Categorical slots for the three trend measures (fixed order, never cycled).
const TREND_SERIES: { key: keyof Omit<TrendPoint, 't'>; label: string; color: string }[] = [
  { key: 'frequency', label: 'Output frequency', color: '#2a78d6' },
  { key: 'current', label: 'Output current', color: '#eb6834' },
  { key: 'power', label: 'Output power', color: '#1baf7a' },
];

const TREND_WINDOW_MS = 10 * 60 * 1000;
const MAX_TREND_POINTS = 900;

const STATE_META: Record<VfdDriveState, { label: string; color: string; icon: any; ring: string }> = {
  running: { label: 'Running', color: VFD_STATUS_COLORS.good, icon: PlayCircle, ring: 'ring-[#0ca30c]/30' },
  stopped: { label: 'Stopped', color: VFD_STATUS_COLORS.neutral, icon: PauseCircle, ring: 'ring-slate-500/20' },
  fault: { label: 'Fault', color: VFD_STATUS_COLORS.critical, icon: AlertTriangle, ring: 'ring-[#d03b3b]/40' },
  offline: { label: 'No communication', color: VFD_STATUS_COLORS.warning, icon: WifiOff, ring: 'ring-[#fab219]/30' },
  waiting: { label: 'Waiting for data', color: VFD_STATUS_COLORS.neutral, icon: Loader2, ring: 'ring-slate-500/20' },
};

const isNum = (v: any): v is number => typeof v === 'number' && Number.isFinite(v);
const fmt = (v: number | null | undefined, d = 1) =>
  isNum(v) ? v.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d }) : '—';

const timeAgo = (ts?: number) => {
  if (!ts) return 'never';
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 2) return 'just now';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return new Date(ts).toLocaleTimeString();
};

// ------------------------------------------------------------------ small pieces

function StatePill({ state, size = 'sm' }: { state: VfdDriveState; size?: 'sm' | 'lg' }) {
  const meta = STATE_META[state];
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border font-semibold ${size === 'lg' ? 'px-3 py-1.5 text-sm' : 'px-2 py-0.5 text-xs'}`}
      style={{ color: meta.color, borderColor: `${meta.color}55`, backgroundColor: `${meta.color}14` }}
    >
      <Icon className={`${size === 'lg' ? 'h-4 w-4' : 'h-3.5 w-3.5'} ${state === 'waiting' ? 'animate-spin' : ''}`} />
      {meta.label}
    </span>
  );
}

/** 240° arc meter. Track is recessive; the value arc carries the measure. */
function ArcGauge({
  value, max, label, unit, decimals = 1, color = '#2a78d6', sub,
}: { value: number | null; max: number; label: string; unit: string; decimals?: number; color?: string; sub?: string }) {
  const size = 200;
  const stroke = 14;
  const r = (size - stroke) / 2 - 4;
  const cx = size / 2;
  const cy = size / 2;
  const start = 150; // degrees, clockwise from +x
  const sweep = 240;
  const frac = isNum(value) && max > 0 ? Math.min(1, Math.max(0, Math.abs(value) / max)) : 0;
  const polar = (deg: number) => {
    const rad = (deg * Math.PI) / 180;
    return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
  };
  const arc = (from: number, to: number) => {
    const [x1, y1] = polar(from);
    const [x2, y2] = polar(to);
    const large = to - from > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
  };
  const ticks = [0, 0.25, 0.5, 0.75, 1];
  const arcLength = (r * sweep * Math.PI) / 180;

  return (
    <div className="flex flex-col items-center">
      <svg viewBox={`0 0 ${size} ${size * 0.86}`} className="w-full max-w-[220px]" role="img" aria-label={`${label}: ${fmt(value, decimals)} ${unit} of ${max} ${unit}`}>
        <path d={arc(start, start + sweep)} fill="none" stroke="currentColor" strokeOpacity={0.12} strokeWidth={stroke} strokeLinecap="round" className="text-foreground" />
        <path
          d={arc(start, start + sweep)}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={arcLength}
          strokeDashoffset={arcLength * (1 - frac)}
          opacity={frac > 0.002 ? 1 : 0}
          style={{ transition: 'stroke-dashoffset 0.5s ease-out' }}
        />
        {ticks.map((t) => {
          const deg = start + sweep * t;
          const rad = (deg * Math.PI) / 180;
          const x = cx + (r - stroke - 8) * Math.cos(rad);
          const y = cy + (r - stroke - 8) * Math.sin(rad);
          return (
            <text key={t} x={x} y={y} textAnchor="middle" dominantBaseline="middle" className="fill-muted-foreground" fontSize={10}>
              {Math.round(max * t)}
            </text>
          );
        })}
        <text x={cx} y={cy - 2} textAnchor="middle" className="fill-foreground" fontSize={34} fontWeight={700}>
          {fmt(value, decimals)}
        </text>
        <text x={cx} y={cy + 22} textAnchor="middle" className="fill-muted-foreground" fontSize={13}>
          {unit}
        </text>
      </svg>
      <div className="-mt-3 text-center">
        <div className="text-sm font-medium">{label}</div>
        {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
      </div>
    </div>
  );
}

function LoadBar({ value, max, label, warn = true }: { value: number | null; max?: number | null; label?: string; warn?: boolean }) {
  if (!isNum(value) || !isNum(max) || max <= 0) return null;
  const pct = Math.abs(value) / max * 100;
  const color = warn && pct > 100 ? VFD_STATUS_COLORS.critical : warn && pct > 90 ? VFD_STATUS_COLORS.warning : '#2a78d6';
  return (
    <div className="mt-3">
      <div className="mb-1 flex justify-between text-[11px] text-muted-foreground">
        <span>{label || 'Load'}</span>
        <span className="tabular-nums">
          {warn && pct > 90 && <AlertTriangle className="mr-1 inline h-3 w-3" style={{ color }} />}
          {fmt(pct, 0)}% of {fmt(max, max < 10 ? 1 : 0)}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, pct)}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

function CalcBadge({ value }: { value?: VfdComputedValue | null }) {
  if (!value?.calc) return null;
  return (
    <span className="rounded border border-border px-1 py-px text-[10px] uppercase tracking-wide text-muted-foreground" title={value.item.note || 'Calculated from other readings'}>
      calc
    </span>
  );
}

function KpiTile({
  icon: Icon, label, value, unit, children,
}: { icon: any; label: string; value?: VfdComputedValue | null; unit?: string; children?: any }) {
  const hasError = value?.state === 'error';
  return (
    <Card className="border-border/60">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon className="h-4 w-4" />
            {label}
          </div>
          <CalcBadge value={value} />
        </div>
        <div className="mt-2 flex items-baseline gap-1.5" title={value?.error || ''}>
          <span className={`text-3xl font-bold tabular-nums ${hasError ? 'text-muted-foreground' : ''}`}>{value?.text ?? '—'}</span>
          <span className="text-sm text-muted-foreground">{unit ?? value?.item.unit ?? ''}</span>
        </div>
        {hasError && <div className="mt-1 truncate text-xs" style={{ color: VFD_STATUS_COLORS.critical }}>{value?.error}</div>}
        {children}
      </CardContent>
    </Card>
  );
}

function TrendChart({ data, series, unit }: { data: TrendPoint[]; series: (typeof TREND_SERIES)[number]; unit: string }) {
  const last = [...data].reverse().find((p) => isNum(p[series.key]))?.[series.key] ?? null;
  const gradientId = `vfd-grad-${series.key}`;
  return (
    <Card className="border-border/60">
      <CardContent className="p-4">
        <div className="mb-2 flex items-baseline justify-between">
          <div className="flex items-center gap-2 text-sm font-medium">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: series.color }} />
            {series.label}
          </div>
          <div className="text-sm tabular-nums text-muted-foreground">{fmt(last as number | null, 2)} {unit}</div>
        </div>
        <div className="h-36">
          {data.length < 2 ? (
            <div className="flex h-full items-center justify-center text-xs text-muted-foreground">Collecting live samples…</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={series.color} stopOpacity={0.25} />
                    <stop offset="100%" stopColor={series.color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="currentColor" strokeOpacity={0.08} vertical={false} />
                <XAxis
                  dataKey="t"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={(t) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  tick={{ fontSize: 10, fill: 'currentColor', opacity: 0.6 }}
                  stroke="currentColor"
                  strokeOpacity={0.15}
                  minTickGap={40}
                />
                <YAxis tick={{ fontSize: 10, fill: 'currentColor', opacity: 0.6 }} stroke="currentColor" strokeOpacity={0.15} width={48} domain={['auto', 'auto']} />
                <Tooltip
                  cursor={{ stroke: 'currentColor', strokeOpacity: 0.3 }}
                  contentStyle={{ background: 'var(--popover)', color: 'var(--popover-foreground)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                  labelFormatter={(t) => new Date(Number(t)).toLocaleTimeString()}
                  formatter={(v: any) => [`${fmt(Number(v), 2)} ${unit}`, series.label]}
                />
                <Area type="monotone" dataKey={series.key} stroke={series.color} strokeWidth={2} fill={`url(#${gradientId})`} dot={false} isAnimationActive={false} connectNulls />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------------------ main

export function VFDDrivesPanel({ clientId, token, initialDrives }: VFDDrivesPanelProps) {
  const [drives, setDrives] = useState<VfdDrive[]>(initialDrives || []);
  const [loading, setLoading] = useState(!initialDrives);
  const [selectedId, setSelectedId] = useState<string | null>(initialDrives?.[0]?.id ?? null);
  const [live, setLive] = useState<Record<string, LiveSnapshot>>({});
  const [status, setStatus] = useState<Record<string, RuntimeStatus>>({});
  const [showRegisters, setShowRegisters] = useState(false);
  const [, setTick] = useState(0);
  const trendsRef = useRef<Record<string, TrendPoint[]>>({});
  const drivesRef = useRef<VfdDrive[]>(drives);
  drivesRef.current = drives;

  useEffect(() => {
    if (initialDrives) return;
    let cancelled = false;
    vfdApi.listForClient(token, clientId)
      .then((list) => {
        if (cancelled) return;
        setDrives(list);
        setSelectedId((prev) => prev ?? list[0]?.id ?? null);
      })
      .catch(() => { /* empty state handles it */ })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [clientId, token, initialDrives]);

  // Re-render every second so "x s ago" and freshness stay honest between packets.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const ws = getWebSocketClient(token, { clientId });

    const onLive = (data: any) => {
      const id = data?.vfdId;
      if (!id) return;
      const ts = new Date(data.timestamp || Date.now()).getTime();
      setLive((prev) => ({
        ...prev,
        [id]: { values: data.values || {}, raws: data.raws || {}, errors: data.errors || {}, comm: data.comm || 'ok', timestamp: ts },
      }));

      const drive = drivesRef.current.find((d) => d.id === id);
      if (drive) {
        const computed = computeVfdValues(drive.items, data.values, data.errors);
        const point: TrendPoint = {
          t: ts,
          frequency: findByRole(drive.items, computed, 'frequency')?.num ?? null,
          current: findByRole(drive.items, computed, 'current')?.num ?? null,
          power: findByRole(drive.items, computed, 'power')?.num ?? null,
        };
        const cutoff = ts - TREND_WINDOW_MS;
        const next = [...(trendsRef.current[id] || []), point].filter((p) => p.t >= cutoff);
        trendsRef.current[id] = next.length > MAX_TREND_POINTS ? next.slice(-MAX_TREND_POINTS) : next;
      }
    };

    const onStatus = (data: any) => {
      const id = data?.vfdId;
      if (!id) return;
      setStatus((prev) => {
        const cur = prev[id];
        const next: RuntimeStatus = { connected: !!data.connected, comm: data.comm, error: data.error, reconnecting: !!data.reconnecting, at: Date.now() };
        if (cur && cur.connected === next.connected && cur.comm === next.comm && cur.error === next.error && Date.now() - cur.at < 4000) return prev;
        return { ...prev, [id]: next };
      });
    };

    ws.on('vfd_live_data', onLive);
    ws.on('vfd_status_update', onStatus);
    ws.connect();
    return () => {
      ws.off('vfd_live_data', onLive);
      ws.off('vfd_status_update', onStatus);
    };
  }, [clientId, token]);

  // Recomputed every render (the 1s tick keeps freshness honest); drive counts are small.
  const driveViews = drives.map((drive) => {
    const snap = live[drive.id];
    const st = status[drive.id];
    const computed = computeVfdValues(drive.items, snap?.values, snap?.errors);
    // 15s floor covers simulation mode's 5s cadence and a slow server tick.
    const staleAfter = Math.max(15000, (drive.pollIntervalMs || 1000) * 6);
    const fresh = !!snap && Date.now() - snap.timestamp < staleAfter;
    const answering = fresh && snap.comm !== 'error';
    const online = answering && st?.connected !== false;
    const state = getDriveState(drive.items, computed, online, !!snap || !!st);
    return { drive, snap, st, computed, online, state };
  });

  const selected = driveViews.find((v) => v.drive.id === selectedId) || driveViews[0];

  if (loading) {
    return (
      <div className="flex h-72 items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading drives…
      </div>
    );
  }

  if (drives.length === 0) {
    return (
      <Card className="p-12 text-center">
        <GaugeIcon className="mx-auto mb-4 h-14 w-14 text-muted-foreground opacity-50" />
        <h3 className="mb-2 text-xl font-semibold">No VFD drives configured</h3>
        <p className="text-muted-foreground">Drives are added by your administrator in the Admin Panel → VFD Drives.</p>
      </Card>
    );
  }

  const counts = driveViews.reduce((acc, v) => { acc[v.state] = (acc[v.state] || 0) + 1; return acc; }, {} as Record<string, number>);
  const totalKw = driveViews.reduce((sum, v) => {
    const p = findByRole(v.drive.items, v.computed, 'power')?.num;
    return v.online && isNum(p) ? sum + p : sum;
  }, 0);

  return (
    <div className="space-y-6">
      {/* Fleet summary */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {[
          { label: 'Drives', value: drives.length, icon: GaugeIcon, color: undefined },
          { label: 'Running', value: counts.running || 0, icon: PlayCircle, color: VFD_STATUS_COLORS.good },
          { label: 'Fault', value: counts.fault || 0, icon: AlertTriangle, color: (counts.fault || 0) > 0 ? VFD_STATUS_COLORS.critical : undefined },
          { label: 'No comms', value: (counts.offline || 0) + (counts.waiting || 0), icon: WifiOff, color: ((counts.offline || 0) > 0) ? VFD_STATUS_COLORS.warning : undefined },
          { label: 'Total output', value: `${fmt(totalKw, 1)} kW`, icon: Zap, color: undefined },
        ].map((s) => (
          <Card key={s.label} className="border-border/60">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="rounded-lg bg-muted p-2" style={s.color ? { color: s.color } : undefined}>
                <s.icon className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">{s.label}</div>
                <div className="text-xl font-bold tabular-nums">{s.value}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Drive selector */}
      {drives.length > 1 && (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {driveViews.map((v) => {
            const isSel = v.drive.id === selected?.drive.id;
            const f = findByRole(v.drive.items, v.computed, 'frequency');
            const a = findByRole(v.drive.items, v.computed, 'current');
            const p = findByRole(v.drive.items, v.computed, 'power');
            return (
              <button
                key={v.drive.id}
                type="button"
                onClick={() => setSelectedId(v.drive.id)}
                className={`rounded-xl border bg-card p-4 text-left transition-all hover:border-primary/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${isSel ? 'border-primary ring-2 ring-primary/30' : 'border-border/60'}`}
                aria-pressed={isSel}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate font-semibold">{v.drive.name}</div>
                    <div className="truncate text-xs text-muted-foreground">{v.drive.location || [v.drive.brand, v.drive.modelName].filter(Boolean).join(' · ') || '—'}</div>
                  </div>
                  <StatePill state={v.state} />
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  {[{ v: f, u: 'Hz' }, { v: a, u: 'A' }, { v: p, u: 'kW' }].map((x, i) => (
                    <div key={i} className="rounded-md bg-muted/60 px-1 py-1.5">
                      <div className="text-sm font-semibold tabular-nums">{v.online ? (x.v?.text ?? '—') : '—'}</div>
                      <div className="text-[10px] text-muted-foreground">{x.u}</div>
                    </div>
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selected && <DriveDetail view={selected} trend={trendsRef.current[selected.drive.id] || []} showRegisters={showRegisters} onToggleRegisters={() => setShowRegisters((s) => !s)} />}
    </div>
  );
}

// ------------------------------------------------------------------ detail

function DriveDetail({
  view, trend, showRegisters, onToggleRegisters,
}: {
  view: { drive: VfdDrive; snap?: LiveSnapshot; st?: RuntimeStatus; computed: Record<string, VfdComputedValue>; online: boolean; state: VfdDriveState };
  trend: TrendPoint[];
  showRegisters: boolean;
  onToggleRegisters: () => void;
}) {
  const { drive, snap, st, computed, online, state } = view;
  const items = drive.items;
  const r = drive.ratings || {};
  const get = (role: any) => findByRole(items, computed, role);
  const freq = get('frequency');
  const rpm = get('rpm');
  const volt = get('voltage');
  const amp = get('current');
  const kw = get('power');
  const pf = get('pf');
  const fault = get('fault');
  const run = get('run');
  const val = (v?: VfdComputedValue | null) => (online && v ? v : v ? { ...v, text: '—', num: null } : null);

  const maxFreq = r.maxFrequency || 50;
  const rpmMax = r.ratedRpm || Math.max(1500, Math.ceil(((rpm?.num || 0) * 1.2) / 100) * 100);
  const coreRoles = new Set(['frequency', 'rpm', 'voltage', 'current', 'power', 'pf', 'run', 'fault']);
  const others = items.filter((it) => !it.hidden && !coreRoles.has(it.role));
  const units: Record<string, string> = { frequency: freq?.item.unit || 'Hz', current: amp?.item.unit || 'A', power: kw?.item.unit || 'kW' };

  const commLabel = online
    ? (snap?.comm === 'partial' ? 'Live · some registers failing' : 'Live')
    : st?.reconnecting ? 'Reconnecting…' : snap ? 'No response from drive' : 'Waiting for bridge';
  const commColor = online ? (snap?.comm === 'partial' ? VFD_STATUS_COLORS.warning : VFD_STATUS_COLORS.good) : VFD_STATUS_COLORS.warning;

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card className={`border-border/60 ring-1 ${STATE_META[state].ring}`}>
        <CardContent className="flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="truncate text-2xl font-bold">{drive.name}</h2>
              <StatePill state={state} size="lg" />
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              {(drive.brand || drive.modelName) && <span>{[drive.brand, drive.modelName].filter(Boolean).join(' · ')}</span>}
              {drive.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{drive.location}</span>}
              {drive.description && <span className="truncate">{drive.description}</span>}
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-3 text-sm">
            <span className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1" style={{ color: commColor, borderColor: `${commColor}55` }} title={st?.error || ''}>
              {online ? <Radio className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
              {commLabel}
            </span>
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <Clock className="h-3.5 w-3.5" /> {timeAgo(snap?.timestamp)}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Fault / comm banners */}
      {online && fault?.flag && (
        <div className="flex items-start gap-3 rounded-lg border p-4" style={{ borderColor: `${VFD_STATUS_COLORS.critical}66`, backgroundColor: `${VFD_STATUS_COLORS.critical}12` }} role="alert">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" style={{ color: VFD_STATUS_COLORS.critical }} />
          <div>
            <div className="font-semibold" style={{ color: VFD_STATUS_COLORS.critical }}>Drive fault — {fault.text}</div>
            <div className="text-sm text-muted-foreground">
              {isNum(fault.code) ? `Fault code ${fault.code}. ` : ''}Check the drive keypad / manual for the fault description and reset the drive locally.
            </div>
          </div>
        </div>
      )}
      {!online && (st?.error || snap) && (
        <div className="flex items-start gap-3 rounded-lg border p-4" style={{ borderColor: `${VFD_STATUS_COLORS.warning}66`, backgroundColor: `${VFD_STATUS_COLORS.warning}10` }}>
          <WifiOff className="mt-0.5 h-5 w-5 shrink-0" style={{ color: VFD_STATUS_COLORS.warning }} />
          <div className="text-sm">
            <div className="font-semibold">Not receiving data from this drive</div>
            <div className="text-muted-foreground">{st?.error || 'Last reading is stale.'} Values below are hidden until communication returns.</div>
          </div>
        </div>
      )}

      {/* Gauges + state */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="border-border/60">
          <CardContent className="p-5">
            <ArcGauge value={online ? freq?.num ?? null : null} max={maxFreq} label="Output frequency" unit={units.frequency} decimals={freq?.item.decimals ?? 2} sub={`0 – ${maxFreq} Hz`} />
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-5">
            <ArcGauge value={online ? rpm?.num ?? null : null} max={rpmMax} label="Motor speed" unit={rpm?.item.unit || 'rpm'} decimals={0} color="#4a3aa7" sub={rpm?.calc ? 'Calculated from frequency' : r.ratedRpm ? `Rated ${r.ratedRpm} rpm` : undefined} />
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="flex h-full flex-col justify-between gap-4 p-5">
            <div>
              <div className="text-sm text-muted-foreground">Run state</div>
              <div className="mt-2 flex items-center gap-3">
                {(() => {
                  const Icon = STATE_META[state].icon;
                  return <Icon className={`h-10 w-10 ${state === 'waiting' ? 'animate-spin' : ''}`} style={{ color: STATE_META[state].color }} />;
                })()}
                <div>
                  <div className="text-2xl font-bold">{online && run ? run.text : STATE_META[state].label}</div>
                  {run?.calc && <div className="text-xs text-muted-foreground">Derived from output frequency</div>}
                </div>
              </div>
            </div>
            <div className="space-y-2 border-t border-border/60 pt-4 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Fault status</span>
                {online && fault ? (
                  <span className="inline-flex items-center gap-1.5 font-semibold" style={{ color: fault.flag ? VFD_STATUS_COLORS.critical : VFD_STATUS_COLORS.good }}>
                    {fault.flag ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                    {fault.text}
                  </span>
                ) : <span className="text-muted-foreground">—</span>}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Link</span>
                <span>{drive.connectionType === 'tcp' ? 'Modbus TCP' : 'Modbus RTU (RS-485)'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Update rate</span>
                <span>{((drive.pollIntervalMs || 1000) / 1000).toFixed(1)} s</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Electrical KPIs */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiTile icon={Zap} label="Output voltage" value={val(volt)} unit={volt?.item.unit || 'V'}>
          <LoadBar value={online ? volt?.num ?? null : null} max={r.ratedVoltage} label="Of rated voltage" warn={false} />
        </KpiTile>
        <KpiTile icon={Activity} label="Output current" value={val(amp)} unit={amp?.item.unit || 'A'}>
          <LoadBar value={online ? amp?.num ?? null : null} max={r.ratedCurrent} label="Motor load" />
        </KpiTile>
        <KpiTile icon={Zap} label="Output power" value={val(kw)} unit={kw?.item.unit || 'kW'}>
          <LoadBar value={online ? kw?.num ?? null : null} max={r.ratedKw} label="Of rated power" />
        </KpiTile>
        <KpiTile icon={GaugeIcon} label="Power factor" value={val(pf)} unit="">
          <LoadBar value={online ? pf?.num ?? null : null} max={1} label="cos φ" warn={false} />
        </KpiTile>
      </div>

      {/* Trends — one measure per chart */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold">Live trend</h3>
          <span className="text-xs text-muted-foreground">Last 10 minutes · this session</span>
        </div>
        <div className="grid gap-4 lg:grid-cols-3">
          {TREND_SERIES.map((s) => (
            <TrendChart key={s.key} data={trend} series={s} unit={units[s.key]} />
          ))}
        </div>
      </div>

      {/* Other parameters */}
      {others.length > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-semibold">Other parameters</h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {others.map((it) => {
              const v = computed[it.name];
              const flagColor = v?.flag !== undefined ? (v.state === 'bad' ? VFD_STATUS_COLORS.critical : v.state === 'good' ? VFD_STATUS_COLORS.good : undefined) : undefined;
              return (
                <Card key={it.id || it.name} className="border-border/60">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-xs text-muted-foreground">{it.name}</span>
                      <CalcBadge value={v} />
                    </div>
                    <div className="mt-1 flex items-baseline gap-1" title={v?.error || ''}>
                      <span className="text-xl font-semibold tabular-nums" style={flagColor ? { color: flagColor } : undefined}>{online ? v?.text ?? '—' : '—'}</span>
                      <span className="text-xs text-muted-foreground">{it.unit}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Register diagnostics */}
      <Card className="border-border/60">
        <button type="button" onClick={onToggleRegisters} className="flex w-full items-center justify-between p-4 text-left text-sm font-semibold">
          <span className="inline-flex items-center gap-2"><Info className="h-4 w-4 text-muted-foreground" /> Register details</span>
          {showRegisters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        {showRegisters && (
          <div className="overflow-x-auto border-t border-border/60">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground">
                <tr className="text-left">
                  <th className="px-4 py-2 font-medium">Parameter</th>
                  <th className="px-4 py-2 font-medium">Register</th>
                  <th className="px-4 py-2 font-medium text-right">Raw</th>
                  <th className="px-4 py-2 font-medium text-right">Value</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it: VfdItem) => {
                  const v = computed[it.name];
                  const raw = snap?.raws?.[it.name];
                  return (
                    <tr key={it.id || it.name} className="border-t border-border/40">
                      <td className="px-4 py-2">{it.name}{it.hidden && <span className="ml-2 text-[10px] text-muted-foreground">(hidden)</span>}</td>
                      <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                        {it.kind === 'derived' ? 'calculated' : it.addr === null || it.addr === undefined ? 'not set' : `FC${it.func || 3} · ${it.addr} (${hexRegister(it.addr)})${it.words === 2 ? ' · 32-bit' : ''}`}
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-xs tabular-nums">{isNum(raw) ? raw : '—'}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{v?.text ?? '—'} <span className="text-xs text-muted-foreground">{it.unit}</span></td>
                      <td className="px-4 py-2 text-xs">
                        {v?.state === 'error'
                          ? <span style={{ color: VFD_STATUS_COLORS.critical }} title={v.error}>✕ {v.error}</span>
                          : v?.state === 'na'
                            ? <span className="text-muted-foreground">{v.error || 'No data'}</span>
                            : <span style={{ color: VFD_STATUS_COLORS.good }}>✓ OK</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {items.some((it) => it.note) && (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Badge variant="outline" className="text-[10px]">calc</Badge>
          Calculated values are estimates from other readings — compare with the drive display.
        </p>
      )}
    </div>
  );
}
